import React, {useEffect, useMemo} from 'react';

import {useGate, useStore} from 'effector-react';
import {TFunction} from 'i18next';
import {useTranslation} from 'react-i18next';
import {useHistory, useLocation, useParams} from 'react-router-dom';

import {Page} from 'components/common/Page';
import {DateRange} from 'components/ui-kit/DateRange';
import {ContentColumns} from 'components/ui-kit/PageComponents';
import {ROUTE} from 'core/routes';
import {Gate, dataState$, periodChanged} from 'models/tracing/detail';
import {TracingDetailFormatted, TracingDetailRaw, TracingNodeStatement} from 'types';
import {formatDate} from 'utils/date';
import {handleNil} from 'utils/handleNil';
import {formatNumber} from 'utils/number';
import {mapValueIfKeyExist} from 'utils/select';

import {
    GraphContainer,
    GraphWrapper,
    StyledBackTo,
    StyledContentContainer,
    Title,
    TitleWrapper,
} from './styled';
import {TraceGraph} from './TraceGraph';

export function convertData(data: TracingDetailRaw | null, t: TFunction): TracingDetailFormatted {
    return {
        avgPrice: handleNil(data?.name, (value) => formatNumber(value)),
        codeGroup: t(
            mapValueIfKeyExist('dictionaries.productGroups.', handleNil(data?.productGroup)),
        ),
        gtin: handleNil(data?.gtin),
        id: handleNil(data?.id),
        manufacturer: handleNil(data?.manufacturer?.name),
        name: handleNil(data?.name),
        qnt: handleNil(data?.name, (value) => formatNumber(value)),
        trademark: handleNil(data?.brand),
    };
}

export function getGraphStatements(t: TFunction): TracingNodeStatement[] {
    return [
        // {
        //     label: t('pages.tracing.start_balance'),
        //     transform: (value) => handleNil(value.startAmount, formatNumber, '0'),
        //     showIf: (d) => !d.isSold && !d.isDropout,
        // },
        {
            label: t('pages.tracing.input'),
            transform: (value) => handleNil(value.inputAmount, formatNumber, '0'),
            showIf: (d) => !d.isSold && !d.isDropout,
        },
        {
            label: t('pages.tracing.output'),
            transform: (value) => handleNil(value.outputAmount, formatNumber, '0'),
            showIf: (d) => !d.isSold && !d.isDropout,
        },
        {
            label: t('pages.tracing.sold'),
            transform: (value) => handleNil(value.soldAmount, formatNumber, '0'),
            showIf: (d) => d.isSold,
        },
        {
            label: t('pages.tracing.dropout'),
            transform: (value) => handleNil(value.dropoutAmount, formatNumber, '0'),
            showIf: (d) => d.isDropout,
        },
        // {
        //     label: t('pages.tracing.end_balance'),
        //     transform: (value) =>
        //         handleNil(
        //             handleNil(value.startAmount || 0, undefined, 0) +
        //                 handleNil(value.inputAmount || 0, undefined, 0) -
        //                 handleNil(value.outputAmount || 0, undefined, 0),
        //             formatNumber,
        //             '0',
        //         ),
        //     showIf: (d) => !d.isSold && !d.isDropout,
        // },
        {
            label: t('pages.tracing.avgPrice'),
            transform: (value) => handleNil(value.sum, (v) => formatNumber(v, false, 2), '0'),
            showIf: (d) => !d.isDropout,
        },
    ];
}

export const TracingDetailPage = () => {
    const {t} = useTranslation();
    const {data, graph, period, isLoadingGraph, error, isLoading} = useStore(dataState$);
    const {id, gtin}: {id: string; gtin: string} = useParams();
    const {search} = useLocation();
    const history = useHistory();
    useGate(Gate, {id, gtin});

    useEffect(() => {
        const query = new URLSearchParams(search);
        const rawFrom = query.get('from');
        const rawTo = query.get('to');
        periodChanged([rawFrom ? new Date(rawFrom) : null, rawTo ? new Date(rawTo) : null] as [
            Date | null,
            Date | null,
        ]);
    }, []);

    useEffect(() => {
        const [from, to] = period;
        const params = new URLSearchParams({
            ...(from && {from: formatDate(from, 'yyyy-MM-dd')}),
            ...(to && {to: formatDate(to, 'yyyy-MM-dd')}),
        });
        history.replace({search: params.toString()});
    }, [period]);

    const mappedData = React.useMemo(() => convertData(data, t), [data, t]);
    const contentColumnsData = useMemo(
        () => [
            {gtin: mappedData.gtin},
            {manufacturer: mappedData.manufacturer},
            {trademark: mappedData.trademark},
            {groupCode: mappedData.codeGroup},
        ],
        [mappedData],
    );

    return (
        <>
            <Page title={t('menu.Tracing')}>
                <StyledContentContainer
                    status={isLoading ? 'loading' : error != null ? 'error' : 'default'}
                >
                    <StyledBackTo to={ROUTE.TRACING}>{t('pages.tracing.back')}</StyledBackTo>
                    <TitleWrapper>
                        <Title>{mappedData.name}</Title>
                        <DateRange value={period} onChange={periodChanged} />
                    </TitleWrapper>
                    <ContentColumns data={contentColumnsData} tNameSpace="pages.tracing" />
                    <GraphWrapper>
                        <GraphContainer
                            status={
                                isLoadingGraph ? 'loading' : error != null ? 'error' : 'default'
                            }
                        >
                            <TraceGraph
                                data={graph}
                                period={period}
                                statements={getGraphStatements}
                            />
                        </GraphContainer>
                    </GraphWrapper>
                </StyledContentContainer>
            </Page>
        </>
    );
};
